import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { useNotification } from '../../notifications/hooks/useNotification';
import type { Terrain } from '../instanceTypes';
import { InstanceApiFactory } from '../api/instanceApi';

interface UseGetTerrainsResult {
  data: Terrain[];
  loading: boolean;
  error: ApiError | Error | null;
}

export function useGetTerrains(): UseGetTerrainsResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => InstanceApiFactory(client), [client]);
  const { notifyError } = useNotification();

  const [data, setData] = useState<Terrain[]>([]);
  const [error, setError] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setError(null);

    api
      .getTerrains({ signal: ctrl.signal })
      .then(terrains => {
        setData(terrains);
      })
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        // No call site displays this error (the dropdowns just end up empty),
        // so every failure notifies globally — unlike the users/* hooks where
        // 4xx are rendered at the call site.
        notifyError(e);
      });

    return () => ctrl.abort();
  }, [api, notifyError]);

  return { data, loading, error };
}
